import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/ui/Button';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { postAuth } from '@/api/ApiCalls';
import { FileReaderHelper } from '@/helpers/fileReaderHelper';
import { LandingSiteFormDtoIn } from '@/models/in/LandingSiteFormDtoIn';
import PopupDialog from './PopupDialog';
import { LANDING_SITE_FORMS_URL, MAX_DOCUMENT_SIZE_MB } from './landingSiteForms';
import styles from './LandingSiteFormsPage.module.scss';

interface ReplaceDocumentDialogProps {
  form: LandingSiteFormDtoIn;
  onClose: () => void;
  onSuccess: () => void;
}

const ReplaceDocumentDialog = ({ form, onClose, onSuccess }: ReplaceDocumentDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pickFile = (file: File | undefined) => {
    if (!file) return;

    setError(null);

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError(t('landingSiteForms.dialog.pdfOnly'));
      setSelectedFile(null);
      return;
    }

    if (file.size > MAX_DOCUMENT_SIZE_MB * 1024 * 1024) {
      setError(t('landingSiteForms.dialog.tooLarge', { max: MAX_DOCUMENT_SIZE_MB }));
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const publish = async () => {
    if (!selectedFile) return;

    setError(null);
    setIsSubmitting(true);

    try {
      const [document] = await FileReaderHelper.fileToDocumentDtoOut(selectedFile);
      await postAuth(
        `${LANDING_SITE_FORMS_URL}/${form.key}/file`,
        { Authorization: `Bearer ${currentUser?.token}` },
        { fileName: document.name, contentBase64: document.contentBase64 }
      );
      onSuccess();
      onClose();
    } catch {
      setError(t('landingSiteForms.dialog.uploadError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PopupDialog title={t('landingSiteForms.dialog.replaceTitle', { name: t(`landingSiteForms.items.${form.key}`) })} onClose={onClose}>
      <div className={styles.dialogBody}>
        <p className={`body-sm-regular ${styles.dialogNote}`}>
          {t('landingSiteForms.dialog.currentDocument', { name: form.fileName })}
        </p>

        <div className={styles.fileRow}>
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isSubmitting}>
            {t('landingSiteForms.dialog.chooseFile')}
          </Button>
          <span className={`body-sm-regular ${styles.fileName}`}>
            {selectedFile ? selectedFile.name : t('landingSiteForms.dialog.noFileChosen')}
          </span>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf"
          className={styles.hiddenFileInput}
          onChange={(e) => {
            pickFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />

        <p className={`body-sm-regular ${styles.dialogWarning}`}>{t('landingSiteForms.dialog.publishWarning')}</p>

        {error && <p className={`body-sm-regular ${styles.error}`}>{error}</p>}

        <div className={styles.dialogActions}>
          <Button variant="white" onClick={onClose} disabled={isSubmitting}>{t('cancel')}</Button>
          <Button variant="primary" onClick={publish} disabled={!selectedFile || isSubmitting}>
            {isSubmitting ? t('saving') : t('landingSiteForms.dialog.publish')}
          </Button>
        </div>
      </div>
    </PopupDialog>
  );
};

export default ReplaceDocumentDialog;
